import { createSlice } from '@reduxjs/toolkit'
import { RootState } from '@/redux/store.ts'
import { getCurrentQuarterRange } from '@/utils/time.ts'
import {
  MOBILE_VIEW_CARD_FIELDS,
  MOBILE_VIEW_CARD_FIELDS_DEFAULT,
  MOBILE_VIEW_CARD_FIELDS_MAX,
  MOBILE_VIEW_CARD_ORDER_DIRECTIONS,
  MOBILE_VIEW_CARD_ORDER_OPTIONS,
  MobileViewCardField,
  MobileViewCardOrderDirection,
  MobileViewCardOrderField,
} from '@/common/constants/idea-list.constants.ts'
import { setUserInfo } from '@/features/user/user.slice.ts'
import { ROLE_CAPTAIN } from '@/router/roles.ts'
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '@/utils/local-storage.ts'
import { IDEA_LIST_MOBILE_CARD_VIEW_KEY } from '@/utils/constants.ts'

type MobileViewCardOrder =
  `${MobileViewCardOrderField}/${MobileViewCardOrderDirection}`

interface MobileViewCardSetting {
  fields: MobileViewCardField[]
  order: MobileViewCardOrder | ''
}

const MOBILE_VIEW_CARD_SETTING_DEFAULT: MobileViewCardSetting = {
  fields: MOBILE_VIEW_CARD_FIELDS_DEFAULT,
  order: '',
}

const includesAll = (stored: unknown, current: readonly string[]) =>
  Array.isArray(stored) && current.every((item) => stored.includes(item))

const isValidOrder = (order: unknown): order is MobileViewCardOrder | '' => {
  if (order === '') return true
  if (typeof order !== 'string') return false
  const [field, direction] = order.split('/')
  return (
    (MOBILE_VIEW_CARD_ORDER_OPTIONS as readonly string[]).includes(field) &&
    (MOBILE_VIEW_CARD_ORDER_DIRECTIONS as readonly string[]).includes(direction)
  )
}

// Like isColumnStateCompatible: drop the saved setting when options were added or values are invalid.
const loadMobileViewCardSetting = (): MobileViewCardSetting => {
  const stored: unknown = getLocalStorageItem(IDEA_LIST_MOBILE_CARD_VIEW_KEY)
  if (stored == null) return MOBILE_VIEW_CARD_SETTING_DEFAULT
  const record = (typeof stored === 'object' ? stored : {}) as Record<
    string,
    unknown
  >
  const { fields, order } = record
  const isCompatible =
    includesAll(record.availableFields, MOBILE_VIEW_CARD_FIELDS) &&
    includesAll(record.availableOrders, MOBILE_VIEW_CARD_ORDER_OPTIONS) &&
    Array.isArray(fields) &&
    fields.length <= MOBILE_VIEW_CARD_FIELDS_MAX &&
    fields.every((field) => MOBILE_VIEW_CARD_FIELDS.includes(field)) &&
    isValidOrder(order)
  if (!isCompatible) {
    removeLocalStorageItem(IDEA_LIST_MOBILE_CARD_VIEW_KEY)
    return MOBILE_VIEW_CARD_SETTING_DEFAULT
  }
  return {
    fields: MOBILE_VIEW_CARD_FIELDS.filter((field) => fields.includes(field)),
    order,
  }
}

const mobileViewCardSetting = loadMobileViewCardSetting()

interface IdeaListInputState {
  category: string
  firm: string
  contributor: string
  dateRange: [string | null, string | null]
  onlyMyIdea: boolean
  showLiquidatedIdeas: boolean
  onlyPremiumLeague: boolean
  bookId: string
  mobileView: 'card' | 'table'
  mobileViewCardOrder: MobileViewCardOrder | ''
  mobileViewCardFields: MobileViewCardField[]
}

const saveMobileViewCard = (state: IdeaListInputState) => {
  setLocalStorageItem(IDEA_LIST_MOBILE_CARD_VIEW_KEY, {
    fields: state.mobileViewCardFields,
    order: state.mobileViewCardOrder,
    availableFields: MOBILE_VIEW_CARD_FIELDS,
    availableOrders: MOBILE_VIEW_CARD_ORDER_OPTIONS,
  })
}

export const ideaListInputSlice = createSlice({
  name: 'ideaListInput',
  initialState: {
    category: '',
    firm: '',
    contributor: '',
    dateRange: getCurrentQuarterRange() as [string, string],
    onlyMyIdea: true,
    showLiquidatedIdeas: false,
    onlyPremiumLeague: false,
    bookId: '',
    mobileView: 'card',
    mobileViewCardOrder: mobileViewCardSetting.order,
    mobileViewCardFields: mobileViewCardSetting.fields,
  } as IdeaListInputState,
  reducers: {
    setIdeaListCategory(state, action) {
      state.category = action.payload
    },
    setIdeaListFirm(state, action) {
      state.firm = action.payload
    },
    setIdeaListContributor(state, action) {
      state.contributor = action.payload
    },
    setIdeaListDateRange(state, action) {
      state.dateRange = [
        action.payload?.[0] ? action.payload[0] : null,
        action.payload?.[1] ? action.payload[1] : null,
      ]
    },
    toggleOnlyMyIdea(state, action) {
      state.onlyMyIdea = action.payload
    },
    toggleShowLiquidatedIdeas(state, action) {
      state.showLiquidatedIdeas = action.payload
    },
    toggleOnlyPremiumLeague(state, action) {
      state.onlyPremiumLeague = action.payload
    },
    setBookId(state, action) {
      state.bookId = action.payload
    },
    setMobileView(state, action) {
      state.mobileView = action.payload
    },
    // Persisted here like toggleTheme, so every dispatch site is covered.
    setMobileViewCardOrder(state, action) {
      state.mobileViewCardOrder = action.payload
      saveMobileViewCard(state)
    },
    setMobileViewCardFields(state, action) {
      state.mobileViewCardFields = action.payload
      saveMobileViewCard(state)
    },
  },
  extraReducers: (builder) => {
    // Only captains can filter to "only my idea"; correct the value as soon as
    // user info loads so the store holds the right value before the idea list
    // mounts and fires its first fetch.
    builder.addCase(setUserInfo, (state, action) => {
      if (action.payload?.groupRole !== ROLE_CAPTAIN) {
        state.onlyMyIdea = false
      }
    })
  },
})

export const selectIdeaListInput = (state: RootState) => state.ideaListInput

export const {
  setIdeaListCategory,
  setIdeaListFirm,
  setIdeaListContributor,
  setIdeaListDateRange,
  toggleOnlyMyIdea,
  toggleShowLiquidatedIdeas,
  toggleOnlyPremiumLeague,
  setBookId,
  setMobileView,
  setMobileViewCardOrder,
  setMobileViewCardFields,
} = ideaListInputSlice.actions
